'use client';

import { use } from 'react';

import { useRouter } from 'next/navigation';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';

import { useLiveTipGoalAmount } from '../../hooks/useLiveTipGoalAmount';
import type { TippingGoalSummary } from '../../types/TippingGoalSummary';
import { TippingHeaderPillView } from './TippingHeaderPillView';

interface Props {
  /** Streamed active goal — created server-side (cached) and unwrapped with `use`. */
  goalPromise: Promise<TippingGoalSummary | null>;
  /** Default pill label when there's no active goal (server-resolved). */
  defaultLabel: string;
  /** The bio urlCode — builds the tipping route href at click time. */
  bioContext: BioContext;
}

/**
 * Interactive header tipping button — a client island whose goal data STREAMS in
 * from the server: the server passes a cached goal promise, which `use()`
 * unwraps (the button lives inside a `<Suspense>` in its slot). Tapping the pill
 * soft-navigates to the `/<urlCode>/tipping` route (the intercepting modal).
 *
 * The streamed goal's `achievedAmount` is overlaid with a live Firestore
 * subscription ({@link useLiveTipGoalAmount}) so the pill's progress updates the
 * instant a tip lands, instead of waiting on the cached promise's cache-life
 * window.
 */
export function TippingHeaderButton({
  goalPromise,
  defaultLabel,
  bioContext,
}: Props) {
  const router = useRouter();
  const goal = use(goalPromise);
  const liveAchievedAmount = useLiveTipGoalAmount(
    bioContext.creatorId,
    goal?.id,
    goal?.achievedAmount ?? 0,
  );
  const liveGoal = goal
    ? { ...goal, achievedAmount: liveAchievedAmount }
    : goal;

  const openTipping = () => {
    router.push(
      bioUrl(bioContext).tipping({ type: 'creator', goalId: goal?.id }),
    );
  };

  return (
    <button
      type="button"
      onClick={openTipping}
      className="block text-left"
      aria-label={goal ? goal.buttonText : defaultLabel}
    >
      <TippingHeaderPillView label={defaultLabel} goal={liveGoal} />
    </button>
  );
}
